// Read measurement and the progress bar.
//
// The reader follows the visit with one IntersectionObserver and a one-second tick, and
// posts what it saw to /t (DigitalMagazine/Data/ReadTrackingService.cs) with sendBeacon:
// every 10 seconds while something changed, and when the page is hidden or closed. Nothing
// here runs per animation frame or talks to the Blazor circuit.
//
// What a visit records:
//   session   active time (tab visible, reader did something in the last 30s), time to
//             first scroll, furthest article, the article on screen when leaving, the
//             progress bar maximum, layout and viewport; with the first beacon also the
//             referrer, screen size and touch points (the endpoint adds the user agent)
//   articles  per article: time it was the one being read, how far into it the reader
//             got (0-100), how often it came back on screen
//   events    CTA clicks, other links, video plays / quartiles / ends, calculator use,
//             lightboxes, menu, article jumps
//   device    a hash of stable browser signals, to estimate unique devices; only where
//             visitor recognition is allowed (docs/visitor-recognition.md)
//
// CTA clicks also land in the clicktrack table the Admin statistics already use; the endpoint
// writes both.

mag.reader = null;

const IDLE_AFTER = 30000;
const FLUSH_EVERY = 10000;
// an article is the one being read once it fills this much of the screen
const CURRENT_MIN_COVERAGE = 0.3;

mag.initReader = (session, token, recognition) => {
    const sections = [...document.querySelectorAll('#wrapper > section[id^="article-"]')];
    const articleId = (el) => Number(el?.id?.slice('article-'.length)) || null;

    const reader = mag.reader = {
        token,
        articles: new Map(),     // id → { coverage, dwell, depth, sentDepth, views }
        events: [],
        progress: 0,
        sentProgress: 0,
        furthest: -1,            // index into sections
        sentFurthest: -1,
        current: null,           // id of the article being read
        active: 0,               // ms since the last beacon
        firstScroll: null,
        lastActivity: performance.now(),
        lastTick: performance.now(),
        started: performance.now(),
        device: null,            // estimate of unique devices, see fingerprint.js
        deviceSent: false,
        sourceSent: false,       // referrer and screen: once per visit
    };
    sections.forEach(el => reader.articles.set(articleId(el), { el, coverage: 0, dwell: 0, depth: 0, sentDepth: 0, views: 0 }));

    // events are recorded with the article they happened in
    mag.track = (type, el, label, value, extra) => {
        if (!reader.token) {
            return;
        }
        const section = el instanceof Element ? el.closest('section[id^="article-"]') : null;
        reader.events.push({ type, article: articleId(section) ?? (typeof el === 'number' ? el : null), label: label ?? null, value: value ?? null, ...extra });
        if (reader.events.length >= 20) {
            flush();
        }
    };

    if (!token) {
        // the dev gallery, or a visitor without a profile: progress bar only
        return;
    }

    const { signal } = session;

    window.magazineDevice?.(recognition).then(device => reader.device = device);

    // ------------------------------------------------------------- what's on screen
    // coverage = share of the viewport an article fills; the largest is the one being read
    const coverage = session.observe(new IntersectionObserver((entries) => {
        const viewport = window.innerWidth * window.innerHeight;
        entries.forEach(entry => {
            const article = reader.articles.get(articleId(entry.target));
            if (article) {
                const rect = entry.intersectionRect;
                article.coverage = entry.isIntersecting ? (rect.width * rect.height) / viewport : 0;
            }
        });
    }, { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }));

    // furthest article: reached once its leading edge passes the middle of the screen —
    // horizontally on desktop, vertically on mobile — as profile.read_article_id always had it
    const reach = session.observe(new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                reader.furthest = Math.max(reader.furthest, sections.indexOf(entry.target));
            }
        });
    }, { rootMargin: '0px -50% -50% 0px' }));

    sections.forEach(el => {
        coverage.observe(el);
        reach.observe(el);
    });

    // ------------------------------------------------------------------- activity
    const activity = () => reader.lastActivity = performance.now();
    ['wheel', 'pointerdown', 'pointermove', 'keydown', 'touchstart'].forEach(type =>
        window.addEventListener(type, activity, { passive: true, signal }));
    window.addEventListener('scroll', () => {
        activity();
        reader.firstScroll ??= Math.round(performance.now() - reader.started);
    }, { passive: true, signal });

    // How far into an article the reader got: the share of it that has been on screen
    // or scrolled past, along the direction the layout moves in.
    const depthOf = (el) => {
        const rect = el.getBoundingClientRect();
        const seen = mag.isMobile()
            ? (window.innerHeight - rect.top) / rect.height
            : (window.innerWidth - rect.left) / rect.width;
        return Math.round(Math.min(Math.max(seen, 0), 1) * 100);
    };

    const tick = () => {
        const now = performance.now();
        const elapsed = Math.min(now - reader.lastTick, 5000);
        reader.lastTick = now;
        if (document.visibilityState !== 'visible' || now - reader.lastActivity > IDLE_AFTER) {
            return;
        }
        reader.active += elapsed;

        let current = null;
        let best = CURRENT_MIN_COVERAGE;
        reader.articles.forEach((article, id) => {
            if (article.coverage >= best) {
                best = article.coverage;
                current = id;
            }
        });
        if (current !== reader.current && current !== null) {
            reader.articles.get(current).views++;
        }
        reader.current = current ?? reader.current;

        if (current !== null) {
            const article = reader.articles.get(current);
            article.dwell += elapsed;
            article.depth = Math.max(article.depth, depthOf(article.el));
        }
    };
    session.timers.push(setInterval(tick, 1000));

    // --------------------------------------------------------------------- events
    // Media events don't bubble, so these listen on capture.
    const videoState = new WeakMap();
    const videoName = (video) => (video.currentSrc || video.src || '').split('/').pop().split('?')[0];
    // a video in an article belongs to it; one in the lightbox to the article that opened it
    const videoOwner = (video) => video.closest('section[id^="article-"]') ? video : (mag.lightboxTrigger ?? video);
    document.addEventListener('play', (e) => {
        const video = e.target;
        if (!(video instanceof HTMLVideoElement)) {
            return;
        }
        const state = videoState.get(video) ?? { quartile: 0, played: false };
        videoState.set(video, state);
        if (!state.played) {
            state.played = true;
            // value: 1 with sound, 0 muted (the autoplaying article videos start muted)
            mag.track('video_play', videoOwner(video), videoName(video), video.muted ? 0 : 1);
        }
    }, { capture: true, signal });
    document.addEventListener('timeupdate', (e) => {
        const video = e.target;
        const state = videoState.get(video);
        if (!(video instanceof HTMLVideoElement) || !state || !video.duration) {
            return;
        }
        const quartile = Math.floor((video.currentTime / video.duration) * 4);
        if (quartile > state.quartile && quartile < 4) {
            state.quartile = quartile;
            mag.track('video_progress', videoOwner(video), videoName(video), quartile * 25);
        }
    }, { capture: true, signal });
    document.addEventListener('ended', (e) => {
        if (e.target instanceof HTMLVideoElement) {
            mag.track('video_end', videoOwner(e.target), videoName(e.target), 100);
        }
    }, { capture: true, signal });

    // calculators: once per input per visit
    const calcUsed = new Set();
    document.addEventListener('input', (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement) || !input.closest('section[id^="article-"]')) {
            return;
        }
        const key = `${input.closest('section').id}|${input.id || input.name}`;
        if (!calcUsed.has(key)) {
            calcUsed.add(key);
            mag.track('calc', input, input.id || input.name || input.type);
        }
    }, { capture: true, signal });

    // CTA buttons carry data-btn-name / data-btn-article. Same name, same rules as the
    // clicktrack rows have always had; a double click on one button counts once.
    let lastCta = { el: null, at: 0 };
    const trackCta = (cta) => {
        const now = performance.now();
        if (lastCta.el === cta && now - lastCta.at < 800) {
            return;
        }
        lastCta = { el: cta, at: now };

        let name = cta.getAttribute('data-btn-name');
        let url = cta.getAttribute('href');
        if (!name) {
            name = (cta.innerText || cta.textContent || '').trim() || 'undefined';
            url ||= 'javascript:void(0);';
        }
        try {
            name = decodeURIComponent(name);
        } catch {
            // not encoded after all
        }
        const article = Number(cta.getAttribute('data-btn-article')) || cta;
        mag.track('cta', article, name, null, { url });
        // the click may well be leaving the page: send it now, a beacon outlives the unload
        flush();
    };

    document.addEventListener('click', (e) => {
        const target = e.target instanceof Element ? e.target : null;
        if (!target) {
            return;
        }
        const cta = target.closest('[data-btn-name]');
        if (cta) {
            trackCta(cta);
        } else {
            // any other link in an article: text links from the CMS, dealer contact details
            const link = target.closest('section[id^="article-"] a[href]');
            const href = link?.getAttribute('href') ?? '';
            if (link && !link.matches('[data-fancybox], a[href*="#article-"]') && !href.startsWith('javascript:') && href !== '#') {
                mag.track('link', link, href.slice(0, 400), null);
                flush();
            }
        }
        const lightbox = target.closest('[data-fancybox]');
        if (lightbox) {
            mag.lightboxTrigger = lightbox;
            mag.track('lightbox', lightbox, lightbox.getAttribute('data-src') || lightbox.getAttribute('href'));
        }
        if (target.closest('.site-header__menu_icon') && !document.body.classList.contains('open-menu')) {
            mag.track('menu', null);
        }
        const jump = target.closest('a[href*="#article-"]');
        if (jump) {
            const to = Number(jump.getAttribute('href').split('#article-')[1]) || null;
            mag.track('nav', jump.closest('.site-header__menu') ? null : jump, jump.closest('.site-header__menu') ? 'menu' : 'article', to);
        }
    }, { capture: true, signal });

    // --------------------------------------------------------------------- beacon
    const flush = () => {
        tick();
        const articles = [];
        reader.articles.forEach((article, id) => {
            const changed = article.dwell > 0 || article.views > 0 || article.depth > article.sentDepth;
            if (changed) {
                articles.push({ id, dwell: Math.round(article.dwell), depth: article.depth, views: article.views });
                article.dwell = 0;
                article.views = 0;
                article.sentDepth = article.depth;
            }
        });
        const progressed = reader.progress > reader.sentProgress || reader.furthest > reader.sentFurthest;
        if (articles.length === 0 && reader.events.length === 0 && !progressed && reader.active < 1000) {
            return;
        }

        const payload = JSON.stringify({
            t: reader.token,
            s: {
                duration: Math.round(performance.now() - reader.started),
                active: Math.round(reader.active),
                firstScroll: reader.firstScroll,
                progress: reader.progress,
                furthest: articleId(sections[reader.furthest]),
                current: reader.current,
                shown: sections.length,
                layout: mag.isMobile() ? 'mobile' : 'desktop',
                vw: window.innerWidth,
                vh: window.innerHeight,
                device: reader.deviceSent ? null : reader.device,
                src: reader.sourceSent ? undefined : {
                    ref: document.referrer || null,
                    sw: screen.width,
                    sh: screen.height,
                    dpr: window.devicePixelRatio,
                    touch: navigator.maxTouchPoints,
                },
            },
            a: articles,
            e: reader.events.splice(0),
        });
        reader.active = 0;
        reader.deviceSent ||= reader.device !== null;
        reader.sourceSent = true;
        reader.sentProgress = reader.progress;
        reader.sentFurthest = reader.furthest;

        // text/plain keeps sendBeacon a simple request; the endpoint parses the JSON itself
        const body = new Blob([payload], { type: 'text/plain' });
        if (!navigator.sendBeacon('/t', body)) {
            fetch('/t', { method: 'POST', body, keepalive: true }).catch(() => {});
        }
    };
    mag.flushReader = flush;

    session.timers.push(setInterval(flush, FLUSH_EVERY));
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flush();
        }
    }, { signal });
    window.addEventListener('pagehide', flush, { signal });
    // a re-run of startBrochure() ends this visit's measurement: send what's left
    signal.addEventListener('abort', flush);
};

// The bar in the header, and the reading progress it stands for (profile.[read]).
mag.initProgress = () => {
    const progressBar = document.getElementById('myProgress');
    const progressBlock = document.getElementById('myProgress-block');

    gsap.to('progress', {
        value: 100,
        ease: 'power1.in',
        scrollTrigger: { scrub: 0.3 },
        onUpdate: () => {
            const val = progressBar ? progressBar.value : 0;
            if (progressBlock) {
                progressBlock.style.height = val + '%';
            }
            if (mag.reader) {
                mag.reader.progress = Math.max(mag.reader.progress, Math.ceil(val));
            }
        }
    });
};
